import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { InvoiceStatusFilter } from '@portal-contract';
import { INVOICE_SEARCH_MAX_LENGTH } from '@portal-contract';
import { Btn } from '@/components/Btn';
import { useI18n } from '@/i18n/I18nProvider';
import { useInvoices } from '@/api/queries';
import { PageHeading } from '@/components/PageHeading';
import { InvoiceList } from '@/components/InvoiceList';
import { EmptyState, ErrorState, LoadingState } from '@/components/States';
import { Field } from '@/components/Field';
import type { MessageKey } from '@/i18n/translate';

const TABS: Array<{ value: InvoiceStatusFilter; key: MessageKey }> = [
  { value: 'open', key: 'invoices.tab.open' },
  { value: 'paid', key: 'invoices.tab.paid' },
  { value: 'all', key: 'invoices.tab.all' },
];
const EMPTY_KEY: Record<InvoiceStatusFilter, MessageKey> = { open: 'empty.invoicesOpen', paid: 'empty.invoicesPaid', all: 'empty.invoices' };
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `/a/:cid/invoices`: Open / Paid / All, number search and date filters. Filters live in the query
 * string (status, q, from, to are not secret) so the browser Back button and reloads work.
 */
export function InvoicesScreen() {
  const { cid = '' } = useParams();
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const status = (['open', 'paid', 'all'] as const).find((s) => s === params.get('status')) ?? 'open';
  const q = (params.get('q') ?? '').slice(0, INVOICE_SEARCH_MAX_LENGTH);
  const from = DAY_RE.test(params.get('from') ?? '') ? (params.get('from') as string) : '';
  const to = DAY_RE.test(params.get('to') ?? '') ? (params.get('to') as string) : '';

  const [qInput, setQInput] = useState(q);
  useEffect(() => setQInput(q), [q]);

  function patch(next: Record<string, string>) {
    const sp = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) sp.set(k, v);
      else sp.delete(k);
    }
    setParams(sp, { replace: true });
  }

  // Commit the search box shortly after typing stops, or immediately on Enter.
  useEffect(() => {
    if (qInput === q) return;
    const id = setTimeout(() => patch({ q: qInput.trim() }), 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  const query = useInvoices(cid, { status, q, from, to });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const nextPageError: unknown = query.isFetchNextPageError ? query.error : null;

  return (
    <div className="flex flex-col gap-500" data-screen="InvoicesScreen">
      <PageHeading>{t('invoices.heading')}</PageHeading>

      <div role="group" aria-label={t('invoices.col.status')} className="flex flex-wrap gap-200">
        {TABS.map((tab) => (
          <Btn
            key={tab.value}
            type="button"
            variant={status === tab.value ? 'default' : 'secondary'}
            size="lg"
            aria-pressed={status === tab.value}
            className="min-h-[44px]"
            onClick={() => patch({ status: tab.value === 'open' ? '' : tab.value })}
          >
            {t(tab.key)}
          </Btn>
        ))}
      </div>

      <form
        role="search"
        className="grid grid-cols-1 gap-300 sm:grid-cols-[2fr_1fr_1fr]"
        onSubmit={(e) => {
          e.preventDefault();
          patch({ q: qInput.trim() });
        }}
      >
        <Field
          label={t('invoices.search')}
          type="search"
          name="q"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={INVOICE_SEARCH_MAX_LENGTH}
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />
        <Field label={t('invoices.from')} type="date" name="from" value={from} max={to || undefined} onChange={(e) => patch({ from: e.target.value })} />
        <Field label={t('invoices.to')} type="date" name="to" value={to} min={from || undefined} onChange={(e) => patch({ to: e.target.value })} />
      </form>

      <div className="min-h-[120px]">
        {query.isPending ? (
          <LoadingState rows={5} />
        ) : query.isError && items.length === 0 ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState>{t(EMPTY_KEY[status])}</EmptyState>
        ) : (
          <div className="flex flex-col items-center gap-400">
            <div className="w-full">
              <InvoiceList items={items} cid={cid} caption={t('invoices.heading')} />
            </div>
            {query.hasNextPage && (
              <Btn type="button" variant="secondary" size="lg" className="min-h-[48px]" loading={query.isFetchingNextPage} disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                {t('invoices.loadMore')}
              </Btn>
            )}
            {nextPageError ? <ErrorState error={nextPageError} onRetry={() => void query.fetchNextPage()} className="w-full" /> : null}
          </div>
        )}
      </div>
    </div>
  );
}
